// iter591 — изнесени от Dashboard.jsx: статус картите на Command Center.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { AlertCircle, Target } from "lucide-react";

// Feb 2026 — human-readable labels for primary_goal values recorded in the
// audit log. Falls back to the raw slug if a new goal is introduced.
const GOAL_LABEL_BG = {
  weight_loss:         "Загуба на тегло",
  muscle_gain:         "Мускулна маса",
  maintenance:         "Поддържане на тегло",
  general_health:      "Общо здраве",
  insulin_resistance:  "Инсулинова резистентност",
  diabetes_type2:      "Диабет тип 2",
};
const GOAL_LABEL_EN = {
  weight_loss:         "Weight loss",
  muscle_gain:         "Muscle gain",
  maintenance:         "Maintenance",
  general_health:      "General health",
  insulin_resistance:  "Insulin resistance",
  diabetes_type2:      "Type 2 diabetes",
};
export function goalLabel(slug, bg) {
  if (!slug) return "";
  const dict = bg ? GOAL_LABEL_BG : GOAL_LABEL_EN;
  return dict[slug] || slug;
}

export function AttentionNeededCard({ user, dailyData, hiSummary, isDark, bg }) {
  // iter577/579 — Triage система + items линкват към съответния модул
  const profile = user?.profile || {};
  const items = [];
  const sleep = dailyData?.sleep;
  if (sleep == null || sleep === 0) {
    items.push({
      priority: "high",
      text: bg ? "Липсва сън за последните 2 дни." : "Sleep missing for 2 days.",
      to: "/dashboard/mindbody-lab",
      cta: bg ? "Към Mind & Body Lab" : "To Mind & Body Lab",
    });
  }
  if (!profile?.body?.weight) {
    items.push({
      priority: "medium",
      text: bg ? "Липсва тегло — за по-точна персонализация." : "Missing weight — needed for personalisation.",
      to: "/dashboard/health-intelligence?tab=body#body-measurements",
      cta: bg ? "Добавете тегло" : "Add weight",
    });
  }
  if (!profile?.body?.height) {
    items.push({
      priority: "medium",
      text: bg ? "Липсва ръст." : "Missing height.",
      to: "/dashboard/health-intelligence?tab=body#body-measurements",
      cta: bg ? "Добави ръст" : "Add height",
    });
  }
  const labs = hiSummary?.totals?.by_type?.lab_panel || 0;
  if (labs < 1) {
    items.push({
      priority: "low",
      text: bg ? "Нови лаборатории могат да повишат точността с +12%." : "New labs would boost accuracy +12%.",
      to: "/dashboard/analyses",
      upload: "standard_lab",
      cta: bg ? "Качете изследване" : "Upload lab",
    });
  }
  if (items.length === 0) {
    items.push({
      priority: "low",
      text: bg ? "Профилът е попълнен. Продължавай със записите." : "Profile complete.",
      to: "/dashboard/profile",
      cta: bg ? "Към профил" : "To profile",
    });
  }

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sorted = items.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  const priorityChip = (p) => {
    const styles = {
      high:   isDark ? "bg-rose-500/20 text-rose-200"     : "bg-rose-100 text-rose-800",
      medium: isDark ? "bg-amber-500/20 text-amber-200"   : "bg-amber-100 text-amber-800",
      low:    isDark ? "bg-sky-500/20 text-sky-200"       : "bg-sky-100 text-sky-800",
    }[p];
    const label = { high: bg ? "Висок" : "High", medium: bg ? "Среден" : "Medium", low: bg ? "Нисък" : "Low" }[p];
    return (
      <span className={`inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${styles}`}>
        {label}
      </span>
    );
  };

  return (
    <section
      data-testid="cc-attention"
      className={`rounded-2xl border p-5 ${
        isDark ? "bg-amber-500/[0.04] border-amber-500/20" : "bg-amber-50 border-amber-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className={`w-4 h-4 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
        <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-amber-200" : "text-amber-900"}`}>
          {bg ? "Има какво да подобрим заедно" : "Room to grow together"}
        </h3>
      </div>
      <ul className={`space-y-2.5 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
        {sorted.slice(0, 4).map((it, i) => (
          <li key={i} className="flex items-start justify-between gap-3" data-testid={`cc-attention-item-${it.priority}`}>
            <div className="flex items-start gap-2.5 min-w-0">
              {priorityChip(it.priority)}
              <span className="leading-relaxed">{it.text}</span>
            </div>
            {it.upload ? (
              <button
                type="button"
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent("app:upload:open",
                      { detail: { hint: it.upload } }));
                  } catch { /* noop */ }
                }}
                className={`shrink-0 text-xs font-bold whitespace-nowrap ${
                  isDark ? "text-amber-300 hover:text-amber-100" : "text-amber-800 hover:text-amber-950"
                }`}
                data-testid="cc-attention-upload-btn"
              >
                {it.cta} →
              </button>
            ) : (
            <Link
              to={it.to}
              className={`shrink-0 text-xs font-bold whitespace-nowrap ${
                isDark ? "text-amber-300 hover:text-amber-100" : "text-amber-800 hover:text-amber-950"
              }`}
            >
              {it.cta} →
            </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HealthJourneyCard({ user, hiSummary, isDark, bg }) {
  const { API } = useAuth();
  // Feb 2026 — Primary-goal change history (audit log from
  // `db.goal_change_audit`). Rendered as milestone markers below the
  // journey bars.
  const [goalHistory, setGoalHistory] = useState([]);
  useEffect(() => {
    if (!API) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/goal-history?limit=10`);
        if (!cancelled) setGoalHistory(Array.isArray(data?.events) ? data.events : []);
      } catch {
        if (!cancelled) setGoalHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  // iter579 — real account age от user.created_at
  let weeksSinceJoin = null;
  if (user?.created_at) {
    try {
      const joined = new Date(user.created_at);
      weeksSinceJoin = Math.max(0, Math.floor((Date.now() - joined.getTime()) / (1000 * 60 * 60 * 24 * 7)));
    } catch { weeksSinceJoin = null; }
  }
  const meals30 = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
  const labs = hiSummary?.totals?.by_type?.lab_panel ?? 0;
  const interventions = hiSummary?.totals?.by_type?.lifestyle_intervention ?? 0;
  const twin = hiSummary?.totals?.by_type?.digital_twin ?? 0;
  const bodyOk = !!(user?.profile?.body?.weight && user?.profile?.body?.height);

  // Three milestones: реални дни от регистрацията + completeness
  const completeness = Math.min(10, Math.round(((bodyOk ? 2 : 0) + Math.min(3, meals30 / 4) + Math.min(2, labs) + Math.min(2, interventions) + Math.min(1, twin))));
  const w1 = weeksSinceJoin == null ? completeness : (weeksSinceJoin >= 1 ? completeness : Math.min(completeness, Math.floor(weeksSinceJoin * 10) + 2));
  const w4 = weeksSinceJoin == null ? completeness : (weeksSinceJoin >= 4 ? completeness : Math.min(completeness, Math.floor((weeksSinceJoin / 4) * 10)));
  const w12 = weeksSinceJoin == null ? completeness : (weeksSinceJoin >= 12 ? completeness : Math.floor((weeksSinceJoin / 12) * 10));

  const bar = (filled, current) => (
    <div className="flex gap-0.5">
      {Array.from({ length: 10 }).map((_, i) => (
        <span
          key={i}
          className={`inline-block w-2.5 h-2.5 rounded-sm ${
            i < filled
              ? (current ? (isDark ? "bg-emerald-400" : "bg-emerald-600") : (isDark ? "bg-violet-400" : "bg-violet-600"))
              : (isDark ? "bg-white/10" : "bg-gray-200")
          }`}
        />
      ))}
    </div>
  );

  const currentWeek = weeksSinceJoin == null ? null : (weeksSinceJoin < 4 ? "w1" : weeksSinceJoin < 12 ? "w4" : "w12");

  return (
    <section
      data-testid="cc-health-journey"
      className={`rounded-2xl border p-5 ${
        isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {bg ? "Вашият напредък" : "Your journey"}
        </h3>
        <span className={`text-[10px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {weeksSinceJoin != null
            ? (bg ? `от ${weeksSinceJoin} ${weeksSinceJoin === 1 ? "седмица" : "седмици"}` : `${weeksSinceJoin}w in`)
            : (bg ? "изграждане на Digital Twin" : "building your Digital Twin")}
        </span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3" data-testid="journey-week-1">
          <span className={`min-w-[80px] text-xs font-semibold ${currentWeek === "w1" ? (isDark ? "text-emerald-300" : "text-emerald-700") : (isDark ? "text-gray-300" : "text-gray-700")}`}>{bg ? "Седмица 1" : "Week 1"}</span>
          {bar(w1, currentWeek === "w1")}
          <span className={`text-[10px] tabular-nums ${isDark ? "text-gray-500" : "text-gray-500"}`}>{w1 * 10}%</span>
        </div>
        <div className="flex items-center gap-3" data-testid="journey-week-4">
          <span className={`min-w-[80px] text-xs font-semibold ${currentWeek === "w4" ? (isDark ? "text-emerald-300" : "text-emerald-700") : (isDark ? "text-gray-300" : "text-gray-700")}`}>{bg ? "Седмица 4" : "Week 4"}</span>
          {bar(w4, currentWeek === "w4")}
          <span className={`text-[10px] tabular-nums ${isDark ? "text-gray-500" : "text-gray-500"}`}>{w4 * 10}%</span>
        </div>
        <div className="flex items-center gap-3" data-testid="journey-week-12">
          <span className={`min-w-[80px] text-xs font-semibold ${currentWeek === "w12" ? (isDark ? "text-emerald-300" : "text-emerald-700") : (isDark ? "text-gray-300" : "text-gray-700")}`}>{bg ? "Седмица 12" : "Week 12"}</span>
          {bar(w12, currentWeek === "w12")}
          <span className={`text-[10px] tabular-nums ${isDark ? "text-gray-500" : "text-gray-500"}`}>{w12 * 10}%</span>
        </div>
      </div>
      <p className={`mt-4 text-[11px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        {bg
          ? "Колкото повече данни събираш, толкова по-точен става твоят персонален здравен модел. Това е път, не резултат."
          : "The more data you log, the sharper your personal model becomes. This is a journey, not a score."}
      </p>

      {/* Feb 2026 — Goal-change milestone timeline. Shows every recorded
          primary_goal transition (from → to, dated). Useful for clinicians
          reviewing patient strategic shifts (weight_loss → maintenance, etc.)
          and for future longitudinal manuscripts. */}
      {goalHistory.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-white/5" data-testid="goal-change-timeline">
          <div className="flex items-center gap-1.5 mb-3">
            <Target className={`w-3.5 h-3.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
            <h4 className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              {bg ? `Промени в целта (${goalHistory.length})` : `Goal changes (${goalHistory.length})`}
            </h4>
          </div>
          <ol className="space-y-2">
            {goalHistory.map((ev, i) => {
              const when = ev.changed_at ? new Date(ev.changed_at) : null;
              const whenStr = when && !isNaN(when.getTime())
                ? when.toLocaleDateString(bg ? "bg-BG" : "en-US", { year: "numeric", month: "short", day: "numeric" })
                : "—";
              const sourceLabel = {
                onboarding_rerun:   bg ? "чрез rerun wizard" : "via rerun wizard",
                onboarding:         bg ? "чрез onboarding" : "via onboarding",
                onboarding_initial: bg ? "при първо onboarding" : "at initial onboarding",
                profile_update:     bg ? "чрез редакция на профил" : "via profile edit",
                progressive_save:   bg ? "чрез автоматично записване" : "via progressive save",
              }[ev.source] || (bg ? "система" : "system");
              return (
                <li
                  key={ev.id || i}
                  data-testid={`goal-change-${i}`}
                  className={`flex items-start gap-2 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  <span className={`mt-0.5 inline-flex w-2 h-2 rounded-full shrink-0 ${isDark ? "bg-violet-400" : "bg-violet-500"}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                        {goalLabel(ev.from_goal, bg)}
                      </span>
                      <span className={isDark ? "text-gray-500" : "text-gray-400"}>→</span>
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${isDark ? "bg-violet-500/20 text-violet-100" : "bg-violet-100 text-violet-900"}`}>
                        {goalLabel(ev.to_goal, bg)}
                      </span>
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {whenStr} · {sourceLabel}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}

export function WelcomeStateCard({ isDark, bg }) {
  return (
    <section
      data-testid="cc-welcome-state"
      className={`rounded-3xl p-6 sm:p-8 border ${
        isDark
          ? "bg-gradient-to-br from-pink-900/20 via-[hsl(222,47%,9%)] to-violet-900/20 border-pink-700/30"
          : "bg-gradient-to-br from-pink-50 via-white to-violet-50 border-pink-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-3xl" aria-hidden="true">👋</span>
        <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Добре дошли в Nutramilo" : "Welcome to Nutramilo"}
        </h2>
      </div>
      <p className={`text-sm sm:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {bg ? "Започнете с:" : "Start with:"}
      </p>
      <ol className={`mt-3 space-y-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
        <li className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-100 text-violet-800"}`}>1</span>
          <span>{bg ? "Запишете първото си хранене." : "Log your first meal."}</span>
        </li>
        <li className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-100 text-violet-800"}`}>2</span>
          <span>{bg ? "Добавете тегло и ръст в профила." : "Add weight & height in your profile."}</span>
        </li>
        <li className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-100 text-violet-800"}`}>3</span>
          <span>{bg ? "Качете изследване, ако имате." : "Upload a lab result if you have one."}</span>
        </li>
      </ol>
      <p className={`mt-4 text-[12px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        {bg
          ? "След това ще започнем да изграждаме вашия персонален здравен модел."
          : "Then we'll start building your personal health model."}
      </p>
      {/* iter592 — Task 7: продуктовата история, видима за новия потребител */}
      <div className={`mt-4 flex flex-wrap items-center gap-1.5 text-[11px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`} data-testid="cc-welcome-story">
        {(bg
          ? ["Събираме данни", "Разбираме организма ви", "Изграждаме дигитален здравен близнак", "Даваме персонализирани действия", "Проследяваме резултатите"]
          : ["We collect data", "We understand your body", "We build your digital health twin", "We give personalised actions", "We track the results"]
        ).map((s, i, arr) => (
          <React.Fragment key={s}>
            <span className={`px-2 py-0.5 rounded-full ${isDark ? "bg-white/5" : "bg-white/80 border border-pink-100"}`}>{s}</span>
            {i < arr.length - 1 && <span className={isDark ? "text-gray-600" : "text-gray-400"} aria-hidden="true">→</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Link
          to="/dashboard/diary"
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${
            isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-100 text-violet-900 hover:bg-violet-200"
          }`}
        >
          📸 {bg ? "Запишете хранене" : "Log meal"}
        </Link>
        <Link
          to="/dashboard/profile"
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold ${
            isDark ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-[#1A1A2E]"
          }`}
        >
          ⚖️ {bg ? "Профилът ми →" : "My profile →"}
        </Link>
      </div>
    </section>
  );
}

export function EcosystemProgressCard({ user, hiSummary, mealDilData, isDark, bg }) {
  // iter576 — Колко добре е изградена картината на пациента, по модул.
  // Не е health score — измерва "data completeness" per ecosystem area.
  const meals30 = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
  const labs = hiSummary?.totals?.by_type?.lab_panel ?? 0;
  const interventions = hiSummary?.totals?.by_type?.lifestyle_intervention ?? 0;
  const twin = hiSummary?.totals?.by_type?.digital_twin ?? 0;
  const profile = user?.profile || {};
  const bodyOk = !!(profile?.body?.weight && profile?.body?.height);

  const rows = [
    {
      key: "nutrition",
      label: bg ? "Хранене" : "Nutrition",
      pct: Math.min(100, Math.round((meals30 / 30) * 100)),
    },
    {
      key: "mindbody",
      label: bg ? "Тяло и ум" : "Mind & Body",
      pct: Math.min(100, Math.round((interventions / 10) * 100)),
    },
    {
      key: "precision",
      label: bg ? "Прецизно здраве" : "Precision Health",
      pct: Math.min(100, Math.round((labs / 3) * 100)),
    },
    {
      key: "twin",
      label: "Digital Twin",
      pct: Math.min(100, Math.round((twin / 3) * 100)),
    },
    {
      key: "body",
      label: bg ? "Лични параметри" : "Body parameters",
      pct: bodyOk ? 100 : 0,
    },
  ];

  return (
    <section
      data-testid="cc-ecosystem-progress"
      className={`rounded-2xl border p-5 ${
        isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {bg ? "Вашата здравна екосистема" : "Your health ecosystem"}
        </h3>
        <span className={`text-[10px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg ? "колко добре е изградена картината" : "how well your picture is built"}
        </span>
      </div>
      <ul className="space-y-2.5">
        {rows.map(r => (
          <li key={r.key} className="flex items-center gap-3" data-testid={`ecosystem-${r.key}`}>
            <span className={`min-w-[140px] text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>{r.label}</span>
            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
              <div
                className={`h-full transition-all duration-500 ${
                  r.pct < 33 ? "bg-rose-400" : r.pct < 66 ? "bg-amber-400" : "bg-emerald-500"
                }`}
                style={{ width: `${Math.max(4, r.pct)}%` }}
              />
            </div>
            <span className={`text-xs font-bold tabular-nums w-10 text-right ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{r.pct}%</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function RecentChangesCard({ hiSummary, isDark, bg }) {
  // iter619 — Truly dynamic feed: fetch real twin trends (DIL/Cognitive/Lab deltas).
  const { API, user } = useAuth();
  const userId = user?.id || user?._id;
  const [trends, setTrends] = React.useState(null);
  const [twinScore, setTwinScore] = React.useState(null);
  const [stress, setStress] = React.useState(null);
  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const [t, ts, st] = await Promise.all([
          axios.get(`${API}/health-twin/trends?days=30`).catch(() => null),
          axios.get(`${API}/health-twin/twin-score?days=30`).catch(() => null),
          axios.get(`${API}/stress-twin/snapshot`).catch(() => null),
        ]);
        if (cancelled) return;
        setTrends(t?.data || null);
        setTwinScore(ts?.data || null);
        setStress(st?.data || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [API, userId]);

  const items = [];

  // 1. DIL delta — show actual percentage change with direction
  const dilDelta = trends?.dil?.delta_pct;
  const dilSamples = trends?.dil?.samples || 0;
  if (typeof dilDelta === "number" && dilSamples >= 2) {
    const arrow = dilDelta < -1.5 ? "📉" : dilDelta > 1.5 ? "📈" : "➡️";
    const sign = dilDelta > 0 ? "+" : "";
    items.push({
      emoji: arrow,
      text: bg
        ? (dilDelta < -1.5
            ? `Инсулиновото натоварване намалява (${sign}${dilDelta}% за 30 дни) — отлично!`
            : dilDelta > 1.5
              ? `Инсулиновото натоварване расте (${sign}${dilDelta}% за 30 дни) — добави фибри.`
              : `Инсулиновото натоварване е стабилно (${sign}${dilDelta}% за 30 дни).`)
        : `Insulin load ${sign}${dilDelta}% over 30 days.`,
    });
  }

  // 2. Meal logging momentum — actual count
  const meals = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
  if (meals > 0) {
    items.push({
      emoji: meals >= 15 ? "🍽️" : "📝",
      text: bg
        ? (meals >= 15
            ? `${meals} хранения за 30 дни — картината става по-ясна.`
            : `${meals} ${meals === 1 ? "записано хранене" : "записани хранения"} за 30 дни — продължавай.`)
        : `${meals} meal${meals === 1 ? "" : "s"} logged in 30 days.`,
    });
  }

  // 3. Cognitive delta
  const cogDelta = trends?.cognitive?.delta_pct;
  const cogSamples = trends?.cognitive?.samples || 0;
  if (typeof cogDelta === "number" && cogSamples >= 2) {
    const arrow = cogDelta > 1.5 ? "🧠" : cogDelta < -1.5 ? "📉" : "➡️";
    const sign = cogDelta > 0 ? "+" : "";
    items.push({
      emoji: arrow,
      text: bg
        ? `Когнитивен резултат: ${sign}${cogDelta}% за 30 дни (${cogSamples} сесии).`
        : `Cognitive score: ${sign}${cogDelta}% over 30 days.`,
    });
  }

  // 4. Lab — HbA1c specifically, with delta
  const hba1cDelta = trends?.lab?.hba1c_pct?.delta_pct;
  const hba1cPoints = trends?.lab?.hba1c_pct?.series || [];
  if (hba1cPoints.length >= 1) {
    const last = hba1cPoints[hba1cPoints.length - 1]?.value;
    if (last != null) {
      const arrow = typeof hba1cDelta === "number"
        ? (hba1cDelta < 0 ? "📉" : hba1cDelta > 0 ? "📈" : "➡️")
        : "🧪";
      const deltaTxt = typeof hba1cDelta === "number" && hba1cPoints.length >= 2
        ? ` (${hba1cDelta > 0 ? "+" : ""}${hba1cDelta}%)`
        : "";
      items.push({
        emoji: arrow,
        text: bg
          ? `HbA1c: ${last}%${deltaTxt}.`
          : `HbA1c: ${last}%${deltaTxt}.`,
      });
    }
  } else {
    const labs = hiSummary?.totals?.by_type?.lab_panel ?? 0;
    if (labs > 0) {
      items.push({
        emoji: "🧪",
        text: bg ? `${labs} ${labs === 1 ? "лабораторен анализ е анализиран" : "лабораторни анализа са анализирани"}.` : `${labs} lab panel${labs === 1 ? "" : "s"} analyzed.`,
      });
    }
  }

  // 5. Twin Score — show real composite
  if (twinScore?.composite != null && (twinScore.coverage_pct || 0) >= 25) {
    items.push({
      emoji: "🧬",
      text: bg
        ? `Twin Score: ${twinScore.composite}/100 (${twinScore.coverage_pct}% покритие).`
        : `Twin Score: ${twinScore.composite}/100 (${twinScore.coverage_pct}% coverage).`,
    });
  }

  // 6. Stress — current band if available
  if (stress?.composite != null) {
    items.push({
      emoji: "⚡",
      text: bg
        ? `Кортизолно натоварване: ${stress.composite}/100 (${stress.band}).`
        : `Cortisol load: ${stress.composite}/100 (${stress.band}).`,
    });
  }

  // Fallback if absolutely no data
  if (items.length === 0) {
    items.push({
      emoji: "✨",
      text: bg
        ? "Запиши първото хранене или направи когнитивен тест — оттук започват промените."
        : "Log your first meal or cognitive test — that's where change begins.",
    });
  }

  return (
    <section
      data-testid="cc-recent-changes"
      className={`rounded-2xl border p-5 ${
        isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl" aria-hidden="true">📈</span>
        <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {bg ? "Последни промени" : "Recent changes"}
        </h3>
      </div>
      <ul className={`space-y-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="cc-recent-changes-list">
        {items.slice(0, 5).map((it, i) => (
          <li key={i} className="flex items-start gap-2.5" data-testid={`cc-recent-change-${i}`}>
            <span className="text-base" aria-hidden="true">{it.emoji}</span>
            <span className="leading-relaxed">{it.text}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/dashboard/health-intelligence"
        className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}
      >
        ➜ {bg ? "Виж пълната история" : "View full history"}
      </Link>
    </section>
  );
}
